import { Button, styled, TextField } from '@mui/material';

export const Details = styled('div')`
  background-color: #ffffff;
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

export const HeaderSection = styled('div')`
  background-color: #d1efff;
  border-radius: 20px;
  padding: 24px 24px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const HeaderTitle = styled('div')`
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 700;
  font-size: 12px;
  line-height: 16.8px;
  letter-spacing: 0.084px;
  text-transform: uppercase;
  color: #000;
  word-break: break-word;
`;

export const HeaderSubtitle = styled('div')`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 19.2px;
  color: #000;
  word-break: break-word;
`;

export const Body = styled('div', { shouldForwardProp: (prop) => prop !== 'horizontal' })<{
  horizontal?: boolean;
}>`
  display: flex;
  gap: ${({ horizontal }) => (horizontal ? '32px' : '24px')};
  flex-direction: ${({ horizontal }) => (horizontal ? 'row' : 'column')};
  flex-wrap: ${({ horizontal }) => (horizontal ? 'wrap' : 'nowrap')};
  align-items: ${({ horizontal }) => (horizontal ? 'flex-start' : 'stretch')};
  justify-content: ${({ horizontal }) => (horizontal ? 'center' : 'flex-start')};
`;

export const PreviewColumn = styled('div', { shouldForwardProp: (prop) => prop !== 'compact' })<{
  compact?: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 300px;
  ${({ compact }) => (compact ? 'flex: 1 1 802px; max-width: 802px;' : '')}
`;

export const ControlsColumn = styled('div', { shouldForwardProp: (prop) => prop !== 'compact' })<{
  compact?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => (compact ? '28px' : '24px')};
  width: 100%;
  ${({ compact }) => (compact ? 'flex: 1 1 662px; max-width: 662px; min-width: 280px;' : '')}
`;

export const Section = styled('div')`
  display: flex;
  flex-direction: column;
`;

export const SubmitSection = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #eee;
`;

export const DocumentLabel = styled('div', { shouldForwardProp: (prop) => prop !== 'muted' && prop !== 'flush' })<{
  muted?: boolean;
  flush?: boolean;
}>`
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 700;
  font-size: 12px;
  line-height: 16.8px;
  letter-spacing: 0.084px;
  text-transform: uppercase;
  padding-left: ${({ flush }) => (flush ? '0' : '24px')};
  padding-bottom: 16px;
  color: ${({ muted }) => (muted ? '#666' : '#000')};
  word-break: break-word;

  .prefix {
    color: #999;
  }
`;

export const SignatureHeading = styled('div')`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 19.2px;
  color: #000;
  padding-bottom: 16px;
`;

export const InfoCard = styled('div')`
  background-color: #f9f9f9;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FieldGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const FieldLabel = styled('label')`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const FieldHint = styled('span')`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 15px;
  color: #999;
  word-break: break-word;
`;

/** Text input matching the signer card aesthetic. Used for template vars and signer wallets. */
export const VarTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#fff',
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#bdbdbd',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#000',
      borderWidth: 1,
    },
    '&.Mui-disabled': {
      backgroundColor: '#f4f4f4',
      '& fieldset': {
        borderColor: '#eee',
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: '11px 14px',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: '#777',
  },
});

export const PrimaryButton = styled(Button)({
  height: 44,
  minWidth: 180,
  backgroundColor: 'black',
  color: 'white',
  width: 'fit-content',
  borderRadius: 20,
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '19.2px',
  textTransform: 'uppercase',
  border: 'none',
  padding: '12px 24px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#333',
    border: 'none',
    color: 'white',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#ccc',
    color: '#666',
  },
});

export const ProcessingButton = styled(Button)({
  height: 44,
  minWidth: 180,
  backgroundColor: '#333',
  color: 'white',
  borderRadius: 20,
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '19.2px',
  textTransform: 'uppercase',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    border: 'none',
    backgroundColor: '#333',
    boxShadow: 'none',
  },
});

export const OutlinedButton = styled(Button)({
  height: 44,
  minWidth: 180,
  width: 'fit-content',
  backgroundColor: 'transparent',
  color: '#000',
  borderRadius: 20,
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '19.2px',
  textTransform: 'uppercase',
  border: '1px solid #ccc',
  padding: '13px 25px 13px 21px',
  gap: 8,
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    marginRight: 0,
    marginLeft: 0,
  },
  '&:hover': {
    border: '1px solid #999',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#000',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    border: '1px solid rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.38)',
  },
});
